/**
 * Date/Time Formatting Utilities
 * Handles timezone-aware date/time formatting for alerts and other timestamps
 */

/**
 * Format a date in a specific timezone
 * @param {Date|string} date - The date to format
 * @param {string} timezone - IANA timezone identifier (e.g., 'Europe/Prague', 'America/New_York')
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatDateInTimezone(date, timezone = 'UTC', options = {}) {
    if (!date) return '-';

    const dateObj = date instanceof Date ? date : new Date(date);

    if (isNaN(dateObj.getTime())) {
        return '-';
    }

    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
        ...options
    };

    try {
        return new Intl.DateTimeFormat('en-US', {
            ...defaultOptions,
            timeZone: timezone
        }).format(dateObj);
    } catch (error) {
        console.error('Error formatting date in timezone:', error);
        // Fallback to UTC
        return new Intl.DateTimeFormat('en-US', {
            ...defaultOptions,
            timeZone: 'UTC'
        }).format(dateObj);
    }
}

/**
 * Format an alert timestamp in the device's location timezone
 * @param {Date|string} date - The alert timestamp
 * @param {string} locationTimezone - The location's timezone
 * @returns {string} Formatted timestamp with timezone
 */
export function formatAlertTime(date, locationTimezone = 'UTC') {
    if (!date) return '-';

    return formatDateInTimezone(date, locationTimezone, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
    });
}

/**
 * Format a date/time with both local (browser) and location timezones
 * Useful for showing "Device time" vs "Your time"
 * @param {Date|string} date - The timestamp
 * @param {string} locationTimezone - The device location's timezone
 * @returns {Object} Object with localTime and locationTime strings
 */
export function formatDualTimezone(date, locationTimezone = 'UTC') {
    if (!date) return { localTime: '-', locationTime: '-' };

    const dateObj = date instanceof Date ? date : new Date(date);

    if (isNaN(dateObj.getTime())) {
        return { localTime: '-', locationTime: '-' };
    }

    // Browser's local time
    const localTime = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
    }).format(dateObj);

    // Location's time
    const locationTime = formatDateInTimezone(dateObj, locationTimezone, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
    });

    return {
        localTime,
        locationTime,
        isDifferent: localTime !== locationTime
    };
}

/**
 * Get a relative time string (e.g., "2 hours ago")
 * @param {Date|string} date - The date to compare
 * @returns {string} Relative time string
 */
export function formatRelativeTime(date) {
    if (!date) return '-';

    const dateObj = date instanceof Date ? date : new Date(date);

    if (isNaN(dateObj.getTime())) {
        return '-';
    }

    const now = new Date();
    const diffMs = now - dateObj;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) {
        return `${diffSec} second${diffSec !== 1 ? 's' : ''} ago`;
    } else if (diffMin < 60) {
        return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    } else if (diffHour < 24) {
        return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    } else if (diffDay < 7) {
        return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    } else {
        return dateObj.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }
}

/**
 * Format date range with timezone
 * @param {Date|string} startDate 
 * @param {Date|string} endDate 
 * @param {string} timezone 
 * @returns {string} Formatted date range
 */
export function formatDateRange(startDate, endDate, timezone = 'UTC') {
    const start = formatDateInTimezone(startDate, timezone, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });

    const end = formatDateInTimezone(endDate, timezone, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });

    return `${start} - ${end}`;
}

/**
 * Get the current time in a specific timezone
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Current time in the specified timezone
 */
export function getCurrentTimeInTimezone(timezone = 'UTC') {
    return formatDateInTimezone(new Date(), timezone);
}

/**
 * Convert a date/time to a different timezone
 * @param {Date|string} date 
 * @param {string} fromTimezone 
 * @param {string} toTimezone 
 * @returns {Date} Converted date object
 */
export function convertTimezone(date, fromTimezone, toTimezone) {
    const dateObj = date instanceof Date ? date : new Date(date);

    if (isNaN(dateObj.getTime())) {
        return new Date();
    }

    // Get the time string in the target timezone
    const timeString = formatDateInTimezone(dateObj, toTimezone, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });

    return new Date(timeString);
}

export default {
    formatDateInTimezone,
    formatAlertTime,
    formatDualTimezone,
    formatRelativeTime,
    formatDateRange,
    getCurrentTimeInTimezone,
    convertTimezone
};
